import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Brain, Mail, Lock, ArrowRight, AlertCircle, RefreshCw, ShieldAlert, AlertTriangle, Trash2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../hooks/useTheme';

const Login: React.FC = () => {
  const { login, error, isLoading, forceClearAllData, setError, setIsLoading } = useAuth();
  const navigate = useNavigate();
  const { colors } = useTheme();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const [hasCleared, setHasCleared] = useState(false);

  // Handle clear parameter in URL
  useEffect(() => {
    const clear = searchParams.get('clear');
    if (clear === 'true' && !hasCleared) {
      forceClearAllData();
      setHasCleared(true);
      setSearchParams({});
    }
  }, [searchParams, setSearchParams, forceClearAllData, hasCleared]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Reset error state
    setError(null);
    
    // Validate input
    if (!email.trim() || !password.trim()) {
      setError('Por favor ingresa tu email y contraseña');
      return;
    }
    
    try {
      await login(email, password);
      // Only navigate if login was successful
      navigate('/dashboard');
    } catch (error: any) {
      // Error is already handled in AuthContext
      console.error('Login failed:', error);
      
      // Clear password on error
      setPassword('');
      
      // Focus password field
      const passwordInput = document.getElementById('password') as HTMLInputElement;
      if (passwordInput) {
        passwordInput.focus();
      }

      // Ensure loading state is reset
      setIsLoading(false);
    }
  };

  // Disable form while loading
  const isFormDisabled = isLoading;

  const handleEmergencyClear = async () => {
    if (window.confirm('¿Estás seguro que quieres limpiar todos los datos? Esto cerrará tu sesión y limpiará toda la caché.')) {
      await forceClearAllData();
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: colors.base }}>
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Brain className="w-10 h-10" style={{ color: colors.blue }} />
            <h1 className="text-3xl font-bold" style={{ color: colors.text }}>
              Enki AI
            </h1>
          </div>
          <h2 className="text-2xl font-semibold mb-2" style={{ color: colors.text }}>
            Bienvenido de nuevo
          </h2>
          <p style={{ color: colors.subtext0 }}>
            Inicia sesión para continuar tu viaje de aprendizaje
          </p>
        </div>

        <div 
          className="p-8 rounded-xl border"
          style={{ 
            backgroundColor: colors.mantle,
            borderColor: colors.surface0
          }}
        >
          {error && (
            <div 
              className="p-4 mb-6 rounded-lg flex items-start gap-3"
              style={{ 
                backgroundColor: colors.red + '20',
                color: colors.red
              }}
            >
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Error al iniciar sesión</p>
                <p className="text-sm mt-1 opacity-90">{error}</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label 
                htmlFor="email" 
                className="block text-sm font-medium mb-2"
                style={{ color: colors.text }}
              >
                Correo electrónico
              </label>
              <div className="relative">
                <Mail 
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5"
                  style={{ color: colors.subtext0 }}
                />
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isFormDisabled}
                  required
                  className="w-full pl-10 pr-4 py-3 rounded-lg border focus:outline-none focus:ring-2 transition-colors disabled:opacity-50"
                  style={{
                    backgroundColor: colors.base,
                    borderColor: colors.surface1,
                    color: colors.text
                  }}
                  placeholder="Ingresa tu correo"
                />
              </div>
            </div>

            <div>
              <label 
                htmlFor="password" 
                className="block text-sm font-medium mb-2"
                style={{ color: colors.text }}
              >
                Contraseña
              </label>
              <div className="relative">
                <Lock 
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5"
                  style={{ color: colors.subtext0 }}
                />
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isFormDisabled}
                  required
                  className="w-full pl-10 pr-4 py-3 rounded-lg border focus:outline-none focus:ring-2 transition-colors disabled:opacity-50"
                  style={{
                    backgroundColor: colors.base,
                    borderColor: colors.surface1,
                    color: colors.text
                  }}
                  placeholder="Ingresa tu contraseña"
                />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  disabled={isFormDisabled}
                  className="rounded border mr-2 disabled:opacity-50"
                  style={{ borderColor: colors.surface1 }}
                />
                <span className="text-sm" style={{ color: colors.subtext0 }}>
                  Recordarme
                </span>
              </label>
              <Link 
                to="/forgot-password" 
                className="text-sm hover:underline"
                style={{ color: colors.blue }}
              >
                ¿Olvidaste tu contraseña?
              </Link>
            </div>

            <button
              type="submit"
              disabled={isFormDisabled}
              className="w-full py-3 px-4 rounded-lg font-medium text-white transition-all hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
              style={{ backgroundColor: colors.blue }}
            >
              {isLoading ? (
                <>
                  <RefreshCw className="w-5 h-5 animate-spin" />
                  <span>Iniciando sesión...</span>
                </>
              ) : (
                <>
                  <span>Iniciar sesión</span>
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p style={{ color: colors.subtext0 }}>
              ¿No tienes una cuenta?{' '}
              <Link 
                to="/signup" 
                className="font-medium hover:underline"
                style={{ color: colors.blue }}
              >
                Regístrate
              </Link>
            </p>
          </div>

          {/* Emergency clear button */}
          <div className="mt-8 pt-6 border-t" style={{ borderColor: colors.surface0 }}>
            <p className="text-sm mb-4" style={{ color: colors.subtext0 }}>
              ¿Problemas para iniciar sesión? Intenta limpiar los datos de la aplicación:
            </p>
            <button
              onClick={handleEmergencyClear}
              className="w-full py-2 px-4 rounded-lg font-medium transition-all hover:opacity-90 flex items-center justify-center gap-2"
              style={{ 
                backgroundColor: colors.surface0,
                color: colors.subtext0
              }}
            >
              <Trash2 className="w-4 h-4" />
              <span>Limpiar datos</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;